/**
 * 誰能做什麼。前後端唯一的來源。
 *
 * 後端的 403 與前端的 disabled 由**同一份清單**推導，所以兩者不可能講不同的話。
 * 這個檔案同時被 src/（Vite 建置）與 functions/（Pages 建置）引用。
 *
 * 決策背景見 docs/adr/0003-viewer-role-and-capability-list.md。
 */

// types.ts 也 import 這裡的 Role，形成型別上的互相參照。import type 會在編譯時
// 抹掉，所以不會產生執行期的循環相依。
import type { SqliteBool } from "./types.js";

/** 本人能改，監督者只能看（外加戳與競猜兩件寫入，之後的票會加進清單）。 */
export type Role = "owner" | "viewer";

/**
 * 允許清單，以 `"<METHOD> <路徑>"` 為單位。
 *
 * **列出來才准，沒列到一律拒絕。** 這條一鬆就退化成黑名單——新端點漏列
 * 就是默默開放，那正是 _middleware.ts 當初收攏驗證要消除的錯。
 *
 * 用 Map 而不是物件字面值：物件會把 `constructor`、`__proto__` 這類鍵
 * 當成命中，對一份決定放行與否的表來說那是不能接受的取值方式。
 *
 * 不包含登入流程與 /api/health-sync——那些在 _middleware.ts 的
 * WITHOUT_SESSION 裡就先放行了，根本不會走到這裡。
 */
const CAPABILITIES = new Map<Role, ReadonlySet<string>>([
  [
    "owner",
    new Set([
      "GET /api/measurements",
      "GET /api/daily-logs",
      "GET /api/daily-health",
      "GET /api/status",
      "PUT /api/daily-logs",
      /*
       * 手動補登。捷徑壞掉時本人的唯一寫入路徑，見 docs/adr/0006。
       *
       * 監督者沒有這兩筆，理由**不是**「監督者只能 GET」——那條規則早就被
       * 下面的戳與競猜打破了。是因為補登是本人的事：他連公斤數都看不到，
       * 更不會有東西要填。
       */
      "PUT /api/measurements",
      "PUT /api/daily-health",
      // 看得到誰戳了他、誰怎麼猜。這兩件事本人都只能看不能參與。
      "GET /api/cheers",
      "GET /api/predictions",
      "GET /api/race-predictions",
      // 官方完賽成績只有他自己能補。補的是時間，完賽與否由時間推導。
      "GET /api/race-result",
      "PUT /api/race-result"
    ])
  ],
  [
    "viewer",
    new Set([
      "GET /api/measurements",
      "GET /api/daily-logs",
      "GET /api/daily-health",
      // 同步狀態不在裡面：那是設定頁用的，監督者沒有設定頁。

      // 監督者的兩件寫入。能力清單以 (method, 路徑) 為單位、而不是
      // 「viewer 只放行 GET」，理由就是這兩筆。
      "GET /api/cheers",
      "POST /api/cheers",
      "GET /api/predictions",
      "POST /api/predictions",
      "GET /api/race-predictions",
      "POST /api/race-predictions",
      "GET /api/race-result"
    ])
  ]
]);

/** 認得的角色只有清單上這些。session cookie 解出來的角色要先過這一關。 */
export function isRole(value: unknown): value is Role {
  return CAPABILITIES.has(value as Role);
}

/** 路徑要完全相同才算命中，不做前綴比對。 */
export function can(role: unknown, method: string, path: string): boolean {
  const allowed = CAPABILITIES.get(role as Role);
  return allowed ? allowed.has(`${method} ${path}`) : false;
}

/**
 * 備註與保健品只有本人看得到。
 *
 * 兩者都是「與盯關卡無關的私事」：備註是自由文字，保健品則連品項名稱都寫在
 * 打勾旁邊，而且**它本來就不計分**（見 `src/domain/scoring.ts`），遮掉不會讓
 * 監督者少看到任何一項成績。
 *
 * 條件刻意寫成「是本人才給」而不是「是監督者才遮」——兩者在今天等價，但
 * 角色漏傳、拼錯或未來多一種角色時，前者仍然安全，後者會直接把備註漏出去。
 *
 * **只有在伺服器端做才算數**——前端隱藏欄位擋不住直接打開 API 的人。
 * 回傳新陣列，不修改傳進來的資料。
 */
export function redactLogs<T extends { note: string; supplements_done: SqliteBool }>(
  role: unknown,
  logs: T[]
): T[] {
  if (role === "owner") return logs;
  return logs.map(row =>
    row.note === "" && row.supplements_done === 0
      ? row
      : { ...row, note: "", supplements_done: 0 as SqliteBool }
  );
}

/** 遮蔽後仍然要正規化的三個欄位。BMI 不在裡面——它一律不給。 */
const NORMALIZED_FIELDS = ["weight_kg", "body_fat_percent", "lean_body_mass_kg"] as const;

type MeasurementFields = {
  measured_at: string;
  weight_kg: number | null;
  body_fat_percent: number | null;
  lean_body_mass_kg: number | null;
  bmi: number | null;
  source: string;
};

/**
 * 把一個欄位攤到 0–1。
 *
 * `max === min`（只有一筆、或所有值相同）時一律給 0.5——除以零會產生 `NaN`，
 * 而 `NaN` 會讓線圖**靜默畫不出線**，不報錯也沒有畫面提示。
 *
 * 缺值維持 `null`，不補 0：補 0 的話線圖會畫出一條掉到底的假線。
 */
function normalizeField<T extends MeasurementFields>(
  rows: T[],
  key: (typeof NORMALIZED_FIELDS)[number]
): (number | null)[] {
  const values = rows
    .map(row => row[key])
    .filter((value): value is number => Number.isFinite(value as number));

  if (!values.length) return rows.map(() => null);

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;

  return rows.map(row =>
    Number.isFinite(row[key] as number) ? (span === 0 ? 0.5 : ((row[key] as number) - min) / span) : null
  );
}

/**
 * 揭曉日之前，監督者拿到的是**無單位的 0–1**，不是公斤。
 *
 * 條件寫成「是本人或已揭曉才給原值」而不是「是監督者才遮」——與 `redactLogs`
 * 同一條理由：角色漏傳、拼錯或未來多一種角色時，前者仍然安全，後者會直接
 * 把公斤數漏出去。
 *
 * **只有在伺服器端做才算數**——前端隱藏欄位擋不住直接打開 API 的人。
 * 回傳新陣列，不修改傳進來的資料。
 *
 * 正規化是仿射變換，所以移動平均與趨勢線在正規化空間裡照樣成立，線圖的
 * 形狀完整保留。而位移與尺度兩個未知數，只靠監督者手上那一個已知參考點
 * （起始體重）解不出來——這就是遮蔽能成立的理由，見 `docs/adr/0004`。
 *
 * BMI 一律 `null`：它與體重差一個固定的身高平方，起始體重已知就解得出身高，
 * 之後每天的 BMI 都能還原成公斤。它沒有非數字的呈現方式，所以直接不給。
 *
 * `source` 一律清空，與 `measured_at` 同一類：不是數字，但也沒有理由給。它
 * 在手動補登出現之前一律是 `'InBody'` 所以看起來無害，現在它會說出「這天是
 * 他自己打的」——揭曉日之前監督者不該知道這件事，揭曉日之後隨數字一起解禁。
 */
export function redactMeasurements<T extends MeasurementFields>(
  role: unknown,
  rows: T[],
  revealed: boolean
): { rows: T[]; redacted: boolean } {
  if (role === "owner" || revealed) return { rows, redacted: false };

  const normalized = Object.fromEntries(
    NORMALIZED_FIELDS.map(key => [key, normalizeField(rows, key)])
  ) as Record<(typeof NORMALIZED_FIELDS)[number], (number | null)[]>;

  return {
    redacted: true,
    rows: rows.map((row, index) => ({
      ...row,
      weight_kg: normalized.weight_kg[index],
      body_fat_percent: normalized.body_fat_percent[index],
      lean_body_mass_kg: normalized.lean_body_mass_kg[index],
      bmi: null,
      measured_at: "",
      source: ""
    }))
  };
}
